/**
 * View-model hook for outline auto-hide.
 *
 * Tracks whether the outline component reports displayable nodes. When the
 * outline is collapsed (unmounted), it can't report, so we optimistically
 * fall back to eventNodes.length > 0 to keep the toggle enabled.
 *
 * Auto-hides the outline when content has no nodes (e.g. utility agent)
 * without touching the user's persistent preference. When the user navigates
 * back to an agent with outline content, the preference is still intact.
 */

import { useCallback, useState } from "react";

import type { EventNode } from "../types";

export interface UseOutlineAutoHideOptions {
  /** The EventNode tree the outline derives from. */
  eventNodes: EventNode[];
  /** Whether the layout has an outline configured at all. */
  hasOutline: boolean;
  /** The user's persistent collapsed preference (undefined without outline). */
  outlineCollapsed: boolean | undefined;
}

export interface OutlineAutoHide {
  /** Whether the outline column renders collapsed (preference or auto-hide). */
  isOutlineCollapsed: boolean;
  /** Whether the outline has (or is presumed to have) displayable nodes. */
  outlineHasNodes: boolean;
  /** Callback for the outline's onHasNodesChange report. */
  onOutlineHasNodesChange: (hasNodes: boolean) => void;
}

export function useOutlineAutoHide(
  options: UseOutlineAutoHideOptions
): OutlineAutoHide {
  const { eventNodes, hasOutline, outlineCollapsed } = options;

  const [reportedHasNodes, setReportedHasNodes] = useState(true);

  // Reset to optimistic when eventNodes change (e.g. agent selection changes).
  // Uses "adjust state during render" pattern to avoid an extra effect cycle.
  const [prevEventNodes, setPrevEventNodes] = useState(eventNodes);
  if (prevEventNodes !== eventNodes) {
    setPrevEventNodes(eventNodes);
    if (!reportedHasNodes) {
      setReportedHasNodes(true);
    }
  }

  const hasMatchingEvents = eventNodes.length > 0;
  const autoHidden = hasOutline
    ? !reportedHasNodes && !outlineCollapsed
    : false;
  const isOutlineCollapsed = !hasOutline || !!outlineCollapsed || autoHidden;

  const outlineHasNodes = isOutlineCollapsed
    ? hasMatchingEvents
    : reportedHasNodes;
  const onOutlineHasNodesChange = useCallback((hasNodes: boolean) => {
    setReportedHasNodes(hasNodes);
  }, []);

  return { isOutlineCollapsed, outlineHasNodes, onOutlineHasNodesChange };
}
